import { requestUrl, IT_SERVICES } from "@/lib/requestContext";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, ChevronRight, Users } from "lucide-react";
import { Link, useParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import ExpertCard from "@/components/ExpertCard";
import NotFound from "@/pages/NotFound";
import { CANONICAL_ORIGIN } from "@/lib/seo";
import { getAnalyticsSessionId, trackEvent } from "@/lib/analytics";

function Breadcrumbs({ page }) {
  const parent = page.page_type === "service"
    ? page.related_pages?.find((item) => item.page_type === "category")
    : null;
  return (
    <nav aria-label="Brødkrummer" className="flex flex-wrap items-center gap-1.5 text-xs text-brand-muted">
      <Link to="/">Forside</Link><ChevronRight size={13}/>
      {parent && <><Link to={`/hjaelp/${parent.slug}`}>{parent.name}</Link><ChevronRight size={13}/></>}
      <span aria-current="page" className="text-brand-body">{page.name}</span>
    </nav>
  );
}

export default function SeoLandingPage() {
  const { slug } = useParams();
  const { search } = useLocation();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setPage(null);
    setMissing(false);
    api.get(`/seo-landings/${slug}`).then(({ data }) => setPage(data)).catch((error) => {
      if (error.response?.status === 404) setMissing(true);
    });
  }, [slug]);

  useEffect(() => {
    if (!page) return;
    trackEvent(
      "seo_landing_view",
      { category: page.category_id, metadata: { slug: page.slug, page_type: page.page_type } },
      `seo-landing:${page.slug}:${getAnalyticsSessionId()}`,
    );
  }, [page]);

  const schemas = useMemo(() => {
    if (!page) return [];
    const parent = page.page_type === "service"
      ? page.related_pages?.find((item) => item.page_type === "category")
      : null;
    const crumbs = [
      { "@type": "ListItem", position: 1, name: "Forside", item: `${CANONICAL_ORIGIN}/` },
      ...(parent ? [{ "@type": "ListItem", position: 2, name: parent.name, item: `${CANONICAL_ORIGIN}/hjaelp/${parent.slug}` }] : []),
      { "@type": "ListItem", position: parent ? 3 : 2, name: page.name, item: `${CANONICAL_ORIGIN}/hjaelp/${page.slug}` },
    ];
    return [
      { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs },
      ...(page.faq?.length ? [{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: page.faq.map((item) => ({
          "@type": "Question", name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      }] : []),
    ];
  }, [page]);

  if (missing) return <NotFound/>;
  if (!page) return <div className="mx-auto min-h-[55vh] max-w-6xl animate-pulse px-5 py-14"><div className="h-7 w-48 rounded bg-brand-line"/><div className="mt-7 h-14 max-w-xl rounded bg-brand-line"/></div>;

  const cta = requestUrl(page, search);
  const isIT = IT_SERVICES.has(page.slug);
  const parent = page.page_type === "service"
    ? page.related_pages?.find((item) => item.page_type === "category")
    : null;
  const related = (page.related_pages || []).filter((item) => item.page_type === "service" && item.slug !== page.slug);
  const startRequest = () => trackEvent("seo_landing_request_started", {
    category: page.category_id,
    metadata: { slug: page.slug, page_type: page.page_type },
  });

  return (
    <>
      <Seo title={page.seo_title} description={page.meta_description} path={`/hjaelp/${page.slug}`} schemas={schemas}/>
      <main>
        <section className="border-b border-brand-line bg-[radial-gradient(circle_at_80%_20%,rgba(47,128,110,.08),transparent_38%)]">
          <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8 sm:py-16">
            <Breadcrumbs page={page}/>
            <div className="mt-7 max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[.16em] text-brand-fresh">Personlig hjælp online</p>
              <h1 className="mt-3 text-4xl font-bold tracking-[-.04em] text-brand-ink sm:text-5xl">{page.h1}</h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-brand-body">{page.intro}</p>
              {isIT && <p className="mt-4 text-sm leading-6 text-brand-body">Beskriv problemet · Modtag tilbud · Få hjælp via chat/video</p>}
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link onClick={startRequest} to={cta} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-brand-fresh px-5 text-sm font-semibold text-white">
                  Beskriv dit problem <ArrowRight size={16}/>
                </Link>
                <a href="#personer" className="inline-flex min-h-12 items-center justify-center rounded-xl border border-brand-line bg-white px-5 text-sm font-semibold text-brand-ink">Se personer der kan hjælpe</a>
              </div>
              {isIT && <p className="mt-3 text-sm leading-6 text-brand-body">Gratis at sende · Du vælger selv, om du vil acceptere et tilbud.</p>}
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
          <section className="grid gap-7 lg:grid-cols-[.85fr_1.15fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[.14em] text-brand-fresh">Konkrete situationer</p>
              <h2 className="mt-2 text-2xl font-semibold text-brand-ink">Hvad kan du få hjælp med?</h2>
              <p className="mt-3 text-sm leading-6 text-brand-body">Du behøver ikke kende løsningen på forhånd. Beskriv hvad du vil opnå, hvad der sker, og hvad du allerede har prøvet.</p>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {(page.help_topics || []).map((topic) => <li key={topic} className="flex items-start gap-2.5 rounded-xl border border-brand-line bg-white p-4 text-sm text-brand-body"><CheckCircle2 size={17} className="mt-0.5 shrink-0 text-brand-fresh"/>{topic}</li>)}
            </ul>
          </section>

          <section id="personer" className="mt-14 scroll-mt-24">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div><p className="text-xs font-semibold uppercase tracking-[.14em] text-brand-fresh">Rigtige profiler</p><h2 className="mt-2 text-2xl font-semibold text-brand-ink">Personer der kan hjælpe</h2></div>
              {page.profiles?.length > 0 && <Link to={`/eksperter?category=${page.category_id}`} className="text-sm font-semibold text-brand-fresh">Se alle profiler <ArrowRight size={14} className="inline"/></Link>}
            </div>
            {isIT && <p className="mt-3 text-sm leading-6 text-brand-body"><Link to="/ekspertgodkendelse" className="link-underline">Profiler gennemgås manuelt af SpørgMig, før de bliver offentliggjort.</Link></p>}
            {page.profiles?.length > 0 ? <div className="mt-6 space-y-4">{page.profiles.map((profile) => <ExpertCard key={profile.id} expert={profile}/>)}</div> : (
              <div className="mt-6 rounded-2xl border border-dashed border-brand-line bg-white p-7 sm:flex sm:items-center sm:justify-between sm:gap-6">
                <div className="flex gap-3"><Users className="shrink-0 text-brand-fresh"/><div><h3 className="font-semibold text-brand-ink">Ingen offentlige profiler matcher præcist endnu</h3><p className="mt-1 text-sm leading-6 text-brand-body">Beskriv problemet. Så kan SpørgMig sende forespørgslen til relevante personer, når de er tilgængelige.</p></div></div>
                <Link onClick={startRequest} to={cta} className="mt-5 inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-brand-fresh sm:mt-0">Beskriv problemet <ArrowRight size={15}/></Link>
              </div>
            )}
          </section>

          <section className="mt-14 rounded-2xl bg-brand-soft p-6 sm:p-8">
            <h2 className="text-2xl font-semibold text-brand-ink">Sådan virker SpørgMig</h2>
            <ol className="mt-6 grid gap-5 md:grid-cols-4">
              {[["1", "Beskriv problemet"], ["2", isIT ? "Relevante personer kan sende tilbud" : "Relevante personer svarer og giver tilbud"], ["3", "Sammenlign og vælg"], ["4", "Få hjælp gennem SpørgMig"]].map(([number, text]) => <li key={number} className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-fresh text-xs font-bold text-white">{number}</span><span className="pt-1 text-sm font-medium text-[#334D45]">{text}</span></li>)}
            </ol>
          </section>

          {related.length > 0 && <section className="mt-14"><h2 className="text-xl font-semibold text-brand-ink">{page.page_type === "category" ? "Vælg et område" : "Relateret hjælp"}</h2><div className="mt-4 flex flex-wrap gap-2">{related.map((item) => <Link key={item.slug} to={`/hjaelp/${item.slug}`} className="rounded-full border border-brand-line bg-white px-4 py-2 text-sm text-brand-body hover:border-brand-fresh">{item.name}</Link>)}</div></section>}

          {page.faq?.length > 0 && <section className="mt-14 max-w-3xl"><h2 className="text-2xl font-semibold text-brand-ink">Ofte stillede spørgsmål</h2><div className="mt-5 divide-y divide-brand-line border-y border-brand-line">{page.faq.map((item) => <details key={item.question} className="group py-4"><summary className="cursor-pointer list-none pr-6 font-semibold text-brand-ink">{item.question}</summary><p className="mt-2 max-w-2xl text-sm leading-6 text-brand-body">{item.answer}</p></details>)}</div></section>}

          <section className="mt-14 rounded-2xl bg-brand-green px-6 py-8 text-white sm:flex sm:items-center sm:justify-between sm:gap-6 sm:px-9">
            <div><h2 className="text-2xl font-semibold">Få hjælp til din konkrete situation</h2><p className="mt-2 text-sm text-white/75">{isIT ? "Gratis at sende · Du vælger selv, om du vil acceptere et tilbud." : "Beskriv problemet og se de tilbud, du modtager, før du vælger."}</p></div>
            <Link onClick={startRequest} to={cta} className="mt-5 inline-flex min-h-12 shrink-0 items-center gap-2 rounded-xl bg-white px-5 text-sm font-semibold text-brand-green sm:mt-0">Beskriv dit problem <ArrowRight size={16}/></Link>
          </section>
          {isIT && <p className="mt-4 text-sm text-brand-muted">Spørgsmål om en opgave eller betaling? <Link to="/kontakt" className="link-underline">Kontakt SpørgMig</Link>.</p>}
          {parent && <div className="mt-6 text-sm text-brand-body">Se også <Link className="font-semibold text-brand-fresh" to={`/hjaelp/${parent.slug}`}>{parent.name}</Link>.</div>}
        </div>
      </main>
    </>
  );
}

